import { CopyOutlined } from '@ant-design/icons'
import { Alert, Button, Collapse, Drawer, Empty, Image, Segmented, Tag, Tooltip, message } from 'antd'
import { useMemo, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { platformAIChatAssetContentUrl, siteAssetVariantUrl } from '../api'
import type { AIChatAdminExecutionDetail, AIChatOutputAsset } from '../types'
import { ProviderBadge, StatusBadge, WorkerStatusBadge, formatDateTime } from './adminShared'
import { platformColors } from './theme'

function displayOrDash(value: React.ReactNode): React.ReactNode {
  if (value === null || value === undefined || value === '') return '—'
  return value
}

function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

function durationLabel(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '—'
  if (seconds < 60) return `${seconds.toFixed(1)}s`
  const minutes = Math.floor(seconds / 60)
  const rest = Math.round(seconds % 60)
  return `${minutes}m${rest}s`
}

function copyText(text: string) {
  navigator.clipboard
    ?.writeText(text)
    .then(() => message.success('已复制'))
    .catch(() => message.error('复制失败'))
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', fontSize: 12, gap: 8, padding: '4px 0', borderBottom: `1px solid ${platformColors.border}` }}>
      <div style={{ width: 112, color: platformColors.secondaryText, flexShrink: 0 }}>{label}</div>
      <div style={{ color: platformColors.primaryText, wordBreak: 'break-word', flex: 1 }}>{value}</div>
    </div>
  )
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: 13, fontWeight: 600, color: platformColors.primaryText, margin: '16px 0 8px' }}>
      {children}
    </div>
  )
}

function IdCell({ id }: { id: string }) {
  return (
    <Tooltip title={`${id}（点击复制）`}>
      <span style={{ fontFamily: 'monospace', fontSize: 12, cursor: 'pointer' }} onClick={() => copyText(id)}>
        {id}
      </span>
    </Tooltip>
  )
}

function OutputAssetCard({ asset, workspaceId }: { asset: AIChatOutputAsset; workspaceId: string }) {
  const preview =
    asset.thumbnail_url || asset.download_url || siteAssetVariantUrl(workspaceId, asset.site_asset_id, 'original')
  const full =
    asset.download_url || siteAssetVariantUrl(workspaceId, asset.site_asset_id, 'original')
  return (
    <div className="pf-ai-chat-admin-output-image-link" style={{ marginBottom: 12 }}>
      <Image
        src={preview}
        alt={`output-${asset.ordinal}`}
        className="pf-ai-chat-admin-output-image"
        style={{ maxWidth: '100%', height: 'auto', objectFit: 'contain' }}
        preview={{ src: full }}
      />
      <div className="pf-ai-chat-admin-output-image-meta" style={{ fontSize: 11, marginTop: 6, color: platformColors.secondaryText }}>
        <div>ordinal: {asset.ordinal}</div>
        <div>Asset ID: {asset.site_asset_id}</div>
        <div>
          {asset.content_type} · {asset.width ?? '—'}×{asset.height ?? '—'} · {formatBytes(asset.size_bytes)}
        </div>
        <div>storage_key: {asset.storage_key}</div>
      </div>
    </div>
  )
}

export default function AIChatExecutionDetailDrawer({
  open,
  executionId,
  detail,
  loading,
  error,
  actionBusy,
  onClose,
  onCancel,
  onRetry,
}: {
  open: boolean
  executionId: string | null
  detail: AIChatAdminExecutionDetail | null
  loading: boolean
  error: string | null
  actionBusy: boolean
  onClose: () => void
  onCancel: (id: string) => void
  onRetry: (id: string) => void
}) {
  const [responseView, setResponseView] = useState<'rendered' | 'raw'>('rendered')

  const responseText = detail?.response?.text ?? detail?.response_text ?? ''
  const outputAssets = detail?.response?.output_assets ?? detail?.output_assets ?? []
  const inputAssets = detail?.message?.input_assets ?? []
  const inputText = detail?.message?.text ?? detail?.message_text ?? ''
  const pipeline = detail?.asset_pipeline
  const timeline = detail?.timeline ?? []

  const drawerWidth = useMemo(() => {
    if (typeof window === 'undefined') return 840
    return Math.min(900, Math.max(720, Math.round(window.innerWidth * 0.6)))
  }, [])

  return (
    <Drawer
      title={executionId ? `AI 对话任务详情 — ${executionId.slice(0, 8)}` : 'AI 对话任务详情'}
      open={open}
      onClose={onClose}
      width={drawerWidth}
      destroyOnClose={false}
      styles={{ body: { paddingTop: 8 } }}
    >
      {loading && !detail ? (
        <div style={{ color: platformColors.mutedText, fontSize: 13 }}>加载中…</div>
      ) : error ? (
        <Alert type="error" showIcon message="无法加载任务详情" description={error} />
      ) : !detail ? (
        <Empty description="选择一条任务以查看详情" />
      ) : (
        <div style={{ paddingBottom: 24 }}>
          <SectionTitle>任务概览</SectionTitle>
          <DetailRow label="任务 ID" value={detail.task_id ? <IdCell id={detail.task_id} /> : '—'} />
          <DetailRow label="Execution ID" value={<IdCell id={detail.execution_id} />} />
          <DetailRow label="Conversation ID" value={<IdCell id={detail.conversation_id} />} />
          <DetailRow label="Message ID" value={<IdCell id={detail.message_id} />} />
          <DetailRow label="租户名称" value={displayOrDash(detail.tenant_name ?? detail.conversation?.tenant_name)} />
          <DetailRow label="Workspace" value={displayOrDash(detail.tenant_name)} />
          <DetailRow label="Workspace ID" value={<IdCell id={detail.workspace_id} />} />
          <DetailRow
            label="Provider"
            value={<ProviderBadge provider={detail.provider} label={detail.provider_label} />}
          />
          <DetailRow label="Job Type" value={displayOrDash(detail.job?.job_type)} />
          <DetailRow label="Required Capability" value={displayOrDash(detail.job?.required_capability)} />
          <DetailRow label="状态" value={<StatusBadge status={detail.status} label={detail.status_label} />} />
          <DetailRow
            label="Worker"
            value={
              detail.worker_id || detail.worker?.id ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  {detail.worker_name || detail.worker?.name || (detail.worker_id || detail.worker?.id || '').slice(0, 8)}
                  <WorkerStatusBadge online={detail.worker_online ?? detail.worker?.online ?? undefined} />
                  <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{detail.worker_id || detail.worker?.id}</span>
                </span>
              ) : (
                '—'
              )
            }
          />
          <DetailRow label="创建时间" value={formatDateTime(detail.timing?.created_at ?? detail.created_at)} />
          <DetailRow label="进入队列" value={formatDateTime(detail.timing?.queued_at ?? detail.queued_at)} />
          <DetailRow label="开始时间" value={formatDateTime(detail.timing?.started_at ?? detail.started_at)} />
          <DetailRow label="完成时间" value={formatDateTime(detail.timing?.completed_at ?? detail.completed_at)} />
          <DetailRow
            label="执行耗时"
            value={durationLabel(detail.timing?.execution_duration_seconds ?? detail.execution_duration_seconds)}
          />
          <DetailRow
            label="排队耗时"
            value={durationLabel(detail.timing?.queue_duration_seconds ?? detail.queue_duration_seconds)}
          />
          <DetailRow
            label="Retry / Attempt"
            value={`retry ${detail.retry_count}${detail.is_latest_attempt ? '（最新）' : ''} · attempt ${displayOrDash(detail.attempt_count ?? detail.job?.attempt_count)}`}
          />
          <DetailRow label="错误码" value={displayOrDash(detail.error_code)} />
          <DetailRow label="错误信息" value={displayOrDash(detail.error_message)} />
          {detail.status === 'failed' && (
            <>
              <DetailRow label="FAILED_STAGE" value={displayOrDash(detail.failed_stage ?? detail.job?.error?.failed_stage)} />
              <DetailRow label="RETRYABLE" value={detail.retryable ? '是' : '否'} />
              <DetailRow label="LAST_UPDATED_AT" value={formatDateTime(detail.last_updated_at ?? detail.job?.error?.last_updated_at)} />
            </>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            {detail.cancellable && (
              <Button size="small" danger loading={actionBusy} onClick={() => onCancel(detail.execution_id)}>
                取消
              </Button>
            )}
            {detail.retryable && (
              <Button size="small" loading={actionBusy} onClick={() => onRetry(detail.execution_id)}>
                重试（仅 {detail.provider_label}）
              </Button>
            )}
          </div>

          <SectionTitle>租户输入</SectionTitle>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
            <Button size="small" icon={<CopyOutlined />} onClick={() => copyText(inputText || '')} disabled={!inputText}>
              复制
            </Button>
          </div>
          <div
            style={{
              whiteSpace: 'pre-wrap',
              fontSize: 13,
              background: 'var(--pf-neutral-bg)',
              borderRadius: 6,
              padding: 10,
              maxHeight: 280,
              overflow: 'auto',
            }}
          >
            {inputText || '—'}
          </div>
          <div style={{ marginTop: 12, fontSize: 12, color: platformColors.secondaryText }}>输入图片 / 输入附件</div>
          {inputAssets.length === 0 ? (
            <div style={{ fontSize: 12, color: platformColors.mutedText, marginTop: 6 }}>无输入附件</div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 8 }}>
              {inputAssets.map((asset) => (
                <div
                  key={asset.asset_id}
                  style={{
                    width: 160,
                    border: `1px solid ${platformColors.border}`,
                    borderRadius: 8,
                    padding: 8,
                    background: platformColors.surface,
                  }}
                >
                  <Image
                    src={asset.preview_url || platformAIChatAssetContentUrl(asset.asset_id)}
                    alt={asset.original_filename}
                    style={{ width: '100%', height: 120, objectFit: 'contain' }}
                    preview={{ src: asset.preview_url || platformAIChatAssetContentUrl(asset.asset_id) }}
                  />
                  <div style={{ fontSize: 11, marginTop: 6, wordBreak: 'break-all' }}>
                    <div>{asset.original_filename}</div>
                    <div>{asset.content_type}</div>
                    <div>
                      {asset.width ?? '—'}×{asset.height ?? '—'} · {formatBytes(asset.size_bytes)}
                    </div>
                    <div>Asset ID: {asset.asset_id}</div>
                    <div>Storage: {asset.storage_key}</div>
                    <div>来源: {asset.source}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <SectionTitle>AI 返回结果</SectionTitle>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>A. 返回文字</div>
          {(responseText || '').trim().length > 0 ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <Segmented
                  size="small"
                  value={responseView}
                  onChange={(v) => setResponseView(v as 'rendered' | 'raw')}
                  options={[
                    { label: 'Markdown 渲染', value: 'rendered' },
                    { label: '查看原始文本', value: 'raw' },
                  ]}
                />
                <Button size="small" icon={<CopyOutlined />} onClick={() => copyText(responseText)}>
                  复制
                </Button>
              </div>
              <div
                style={{
                  fontSize: 13,
                  background: 'var(--pf-neutral-bg)',
                  borderRadius: 6,
                  padding: 10,
                  maxHeight: 320,
                  overflow: 'auto',
                }}
              >
                {responseView === 'raw' ? (
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{responseText}</pre>
                ) : (
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{responseText}</ReactMarkdown>
                )}
              </div>
            </>
          ) : (
            <div style={{ fontSize: 12, color: platformColors.mutedText, marginBottom: 10 }}>（无返回文字 — image-only 为合法状态）</div>
          )}

          <div style={{ fontSize: 12, fontWeight: 600, margin: '12px 0 6px' }}>
            B. 返回图片 / Output Assets（{outputAssets.length}）
          </div>
          {outputAssets.length === 0 ? (
            <div style={{ fontSize: 12, color: platformColors.mutedText }}>无返回图片</div>
          ) : (
            <div className="pf-ai-chat-admin-output-images">
              {outputAssets.map((asset) => (
                <OutputAssetCard key={`${asset.site_asset_id}-${asset.ordinal}`} asset={asset} workspaceId={detail.workspace_id} />
              ))}
            </div>
          )}

          <Collapse
            style={{ marginTop: 16 }}
            items={[
              {
                key: 'worker-result',
                label: 'Worker 返回数据',
                children: detail.job?.result ? (
                  <pre
                    style={{
                      margin: 0,
                      fontSize: 11,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      background: 'var(--pf-neutral-bg)',
                      padding: 10,
                      borderRadius: 6,
                      maxHeight: 360,
                      overflow: 'auto',
                    }}
                  >
                    {JSON.stringify(detail.job.result, null, 2)}
                  </pre>
                ) : (
                  <span style={{ fontSize: 12, color: platformColors.mutedText }}>—</span>
                ),
              },
            ]}
          />

          <SectionTitle>结果资产</SectionTitle>
          {pipeline ? (
            <>
              <DetailRow label="Worker 返回图片" value={pipeline.worker_output_count} />
              <DetailRow label="Execution Output Asset" value={pipeline.execution_asset_count} />
              <DetailRow label="Canonical SiteAsset" value={pipeline.canonical_asset_count} />
              <DetailRow
                label="资产链路"
                value={
                  <Tag color={pipeline.consistent ? 'green' : 'red'}>
                    {pipeline.consistent ? '完整' : `不完整${pipeline.gaps.length ? `（缺：${pipeline.gaps.join(', ')}）` : ''}`}
                  </Tag>
                }
              />
            </>
          ) : (
            <div style={{ fontSize: 12, color: platformColors.mutedText }}>—</div>
          )}

          <SectionTitle>执行链路 / Timeline</SectionTitle>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {timeline.map((stage) => (
              <div key={stage.key} style={{ display: 'flex', gap: 8, fontSize: 12, alignItems: 'baseline' }}>
                <span style={{ width: 140, color: platformColors.secondaryText }}>{stage.label}</span>
                <span style={{ flex: 1, color: platformColors.primaryText }}>
                  {stage.status === 'recorded' && stage.at ? formatDateTime(stage.at) : '未记录'}
                </span>
              </div>
            ))}
          </div>

          {detail.status === 'failed' && detail.job?.error && (
            <>
              <SectionTitle>技术详情（失败）</SectionTitle>
              <DetailRow label="ERROR_CODE" value={displayOrDash(detail.job.error.error_code)} />
              <DetailRow label="ERROR_MESSAGE" value={displayOrDash(detail.job.error.error_message)} />
              <DetailRow label="FAILED_STAGE" value={displayOrDash(detail.job.error.failed_stage)} />
              <DetailRow label="ATTEMPT" value={displayOrDash(detail.job.error.attempt)} />
            </>
          )}
        </div>
      )}
    </Drawer>
  )
}
